import React, { useState, useEffect } from 'react';
import ProtectedLayout from '../components/ProtectedLayout';
import api from '../api';
import { toast } from 'sonner';

const CRM_ICONS = {
  gohighlevel: 'fas fa-rocket',
  agencyzoom: 'fas fa-search-dollar',
  salesforce: 'fas fa-cloud',
  hubspot: 'fas fa-hubspot',
  activecampaign: 'fas fa-envelope-open-text',
  mailchimp: 'fas fa-mail-bulk',
  zoho: 'fas fa-briefcase',
  zapier: 'fas fa-bolt',
};

export default function IntegrationsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null); // crm key being added

  const fetchData = async () => {
    try {
      const res = await api.get('/integrations');
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSetup = async (integration) => {
    setAdding(integration.key);
    try {
      await api.post('/integrations/add', { crm: integration.crm, title: integration.title });
      toast.success(`${integration.title} integration added!`);
      await fetchData();
    } catch (e) {
      toast.error('Failed to add integration.');
    } finally {
      setAdding(null);
    }
  };

  const handleDelete = async (integration) => {
    if (!window.confirm(`Are you sure you want to delete ${integration.title || integration.crm_title}?`)) return;
    try {
      await api.delete(`/integrations/${integration.key}`);
      toast.success('Integration deleted.');
      await fetchData();
    } catch (e) {
      toast.error('Failed to delete integration.');
    }
  };

  const handleToggleActive = async (integration) => {
    try {
      await api.put(`/integrations/${integration.key}/settings`, {
        settings: integration.settings || {},
        active: !integration.active,
      });
      toast.success(`Integration ${!integration.active ? 'activated' : 'deactivated'}.`);
      await fetchData();
    } catch (e) {
      toast.error('Failed to update integration.');
    }
  };

  if (loading) {
    return (
      <ProtectedLayout>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px 16px' }}>
          {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: '60px', marginBottom: '12px', borderRadius: '10px' }}></div>)}
        </div>
      </ProtectedLayout>
    );
  }

  const { active_integrations = [], inactive_integrations = [], available_integrations = [] } = data || {};

  return (
    <ProtectedLayout>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px 16px' }}>
        <h1 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '24px', color: '#0F172A' }}>Manage Lead Delivery</h1>

        {/* Active Integrations */}
        {active_integrations.length > 0 && (
          <div className="portal-card" style={{ marginBottom: '20px', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #DDE7F1' }}>
              <h2 style={{ fontWeight: '700', fontSize: '15px', margin: 0 }}>Active Destinations</h2>
            </div>
            {active_integrations.map((integration, i) => (
              <div key={integration.id || i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 20px',
                borderBottom: i < active_integrations.length - 1 ? '1px solid #DDE7F1' : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className={`${CRM_ICONS[integration.crm] || 'fas fa-plug'} text-blue-500`}></i>
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>{integration.title || integration.crm_title}</div>
                    <div style={{ fontSize: '12px', color: '#64748B' }}>
                      <span className="badge badge-green">Active</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    className="btn-secondary"
                    style={{ fontSize: '12px', padding: '5px 12px', height: '32px' }}
                    onClick={() => handleToggleActive(integration)}
                  >
                    Deactivate
                  </button>
                  <button
                    className="btn-secondary"
                    style={{ fontSize: '12px', padding: '5px 12px', height: '32px', color: '#DC2626', borderColor: '#DC2626' }}
                    onClick={() => handleDelete(integration)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Inactive Integrations */}
        {inactive_integrations.length > 0 && (
          <div className="portal-card" style={{ marginBottom: '20px', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #DDE7F1' }}>
              <h2 style={{ fontWeight: '700', fontSize: '15px', margin: 0 }}>Inactive Destinations</h2>
            </div>
            {inactive_integrations.map((integration, i) => (
              <div key={integration.id || i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 20px',
                borderBottom: i < inactive_integrations.length - 1 ? '1px solid #DDE7F1' : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className={`${CRM_ICONS[integration.crm] || 'fas fa-plug'} text-slate-400`}></i>
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>{integration.title || integration.crm_title}</div>
                    <div style={{ fontSize: '12px' }}><span className="badge badge-gray">Inactive</span></div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="btn-secondary"
                    style={{ fontSize: '12px', padding: '5px 12px', height: '32px' }}
                    onClick={() => handleToggleActive(integration)}
                  >
                    Activate
                  </button>
                  <button
                    className="btn-secondary"
                    style={{ fontSize: '12px', padding: '5px 12px', height: '32px', color: '#DC2626', borderColor: '#DC2626' }}
                    onClick={() => handleDelete(integration)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Available Integrations */}
        <div className="portal-card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #DDE7F1' }}>
            <h2 style={{ fontWeight: '700', fontSize: '15px', margin: 0 }}>Available Destinations</h2>
          </div>
          <div style={{ padding: '16px 20px' }}>
            {available_integrations.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#94A3B8', padding: '20px 0', fontSize: '14px' }}>
                All available integrations have been configured.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {available_integrations.map((integration) => (
                  <div key={integration.key} className="portal-card" style={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className={`${CRM_ICONS[integration.crm] || 'fas fa-plug'} text-blue-500 text-sm`}></i>
                      </div>
                      <span style={{ fontWeight: '600', fontSize: '14px' }}>{integration.title}</span>
                    </div>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: '12px', padding: '5px 12px', height: '30px' }}
                      disabled={adding === integration.key}
                      onClick={() => handleSetup(integration)}
                    >
                      {adding === integration.key ? 'Adding...' : 'Set Up'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </ProtectedLayout>
  );
}
